/**
 * 💰 AIRPORT PRICING SERVICE
 * Manages airport transfer pricing for vehicles - Admin operations
 */

import { buildApiUrl } from '@/utils/apiHelpers';

export interface VehicleAirportPricing {
  id: string;
  vehicleId: string;
  hostId: string;
  airportId: string;
  zoneId: string;
  pickupPrice: number; // In kobo (15000 = ₦150.00)
  dropoffPrice: number; // In kobo
  isAvailable: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Populated fields
  airport?: {
    airportCode: string;
    airportName: string;
    city: string;
  };
  zone?: {
    zoneName: string;
    zoneDescription: string;
    distanceRange: {
      min: number;
      max: number;
      display: string;
    };
  };
}

export interface VehicleWithPricing {
  vehicleId: string;
  plateNumber: string;
  vehicleType: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  hostId: string;
  hostName?: string;
  airportPricingCount: number;
  hasAirportPricing: boolean;
  pricings?: VehicleAirportPricing[];
}

export interface SetPricingData {
  zoneId: string;
  pickupPrice: number;
  dropoffPrice: number;
  isAvailable?: boolean;
  notes?: string;
}

export interface UpdatePricingData {
  pickupPrice?: number;
  dropoffPrice?: number;
  isAvailable?: boolean;
  notes?: string;
}

export interface PricingStats {
  totalVehicles: number;
  vehiclesWithPricing: number;
  totalPricingRecords: number;
  avgPickupPrice: number;
  avgDropoffPrice: number;
  byAirport: {
    airportCode: string;
    airportName: string;
    vehicleCount: number;
  }[];
}

export interface PricingHistory {
  id: string;
  pricingId: string;
  action: 'created' | 'updated' | 'deleted' | 'deactivated';
  changedBy: string;
  changedByName?: string;
  changes: Record<string, any>;
  timestamp: string;
}

export interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PricingResponse {
  success: boolean;
  data?: VehicleAirportPricing;
  pricings?: VehicleAirportPricing[];
  vehicles?: VehicleWithPricing[];
  stats?: PricingStats;
  history?: PricingHistory[];
  pagination?: PaginationData;
  error?: string;
  message?: string;
}

export const airportPricingService = {
  /**
   * 📥 Get all vehicles with their airport pricing status
   */
  async getVehiclesWithPricing(filters?: {
    airportId?: string;
    zoneId?: string;
    hasPricing?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PricingResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.airportId) params.append('airportId', filters.airportId);
      if (filters?.zoneId) params.append('zoneId', filters.zoneId);
      if (filters?.hasPricing !== undefined) params.append('hasPricing', String(filters.hasPricing));
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));

      const url = buildApiUrl(`/admin/vehicles/airport-pricing?${params.toString()}`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Vehicles Pricing API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch vehicles (${response.status})`);
      }

      const result = await response.json();

      const vehicles = result.vehicles || result.data?.vehicles || result.data || [];
      const pagination = result.data?.pagination || result.pagination;

      return {
        success: true,
        vehicles: Array.isArray(vehicles) ? vehicles : [],
        pagination: pagination
      };
    } catch (error) {
      console.error('❌ Error fetching vehicles with pricing:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch vehicles'
      };
    }
  },

  /**
   * 📥 Get pricing for a specific vehicle
   */
  async getVehiclePricing(vehicleId: string): Promise<PricingResponse> {
    try {
      const url = buildApiUrl(`/admin/vehicles/${vehicleId}/airport-pricing`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Vehicle Pricing API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch vehicle pricing (${response.status})`);
      }

      const result = await response.json();

      // Handle different backend response formats
      let pricings = result.pricings || result.data?.pricing || result.data?.pricings || [];

      // Ensure we have an array
      if (!Array.isArray(pricings)) {
        pricings = [];
      }

      // Transform flat backend format to nested format expected by frontend
      const transformedPricings = pricings.map((pricing: any) => ({
        ...pricing,
        airport: pricing.airport || {
          airportCode: pricing.airportCode,
          airportName: pricing.airportName,
          city: pricing.city
        },
        zone: pricing.zone || {
          zoneName: pricing.zoneName,
          zoneDescription: pricing.zoneDescription,
          distanceRange: pricing.distanceRange || {
            min: 0,
            max: 0,
            display: ''
          }
        }
      }));

      return {
        success: true,
        pricings: transformedPricings
      };
    } catch (error) {
      console.error('❌ Error fetching vehicle pricing:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch vehicle pricing'
      };
    }
  },

  /**
   * ➕ Set airport transfer pricing for a vehicle
   */
  async setPricing(vehicleId: string, data: SetPricingData): Promise<PricingResponse> {
    try {
      const url = buildApiUrl(`/admin/vehicles/${vehicleId}/airport-pricing`, 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Set Pricing API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to set pricing (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      const pricing = result.data || result.pricing || result;

      return {
        success: true,
        data: pricing,
        message: result.message || 'Pricing set successfully'
      };
    } catch (error) {
      console.error('❌ Error setting pricing:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to set pricing'
      };
    }
  },

  /**
   * ✏️ Update airport transfer pricing
   */
  async updatePricing(pricingId: string, data: UpdatePricingData): Promise<PricingResponse> {
    try {
      const url = buildApiUrl(`/admin/airport-pricing/${pricingId}`, 'v2');

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Pricing API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to update pricing (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      const pricing = result.data || result.pricing || result;

      return {
        success: true,
        data: pricing,
        message: result.message || 'Pricing updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating pricing:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update pricing'
      };
    }
  },

  /**
   * 🗑️ Delete airport transfer pricing
   */
  async deletePricing(pricingId: string): Promise<PricingResponse> {
    try {
      const url = buildApiUrl(`/admin/airport-pricing/${pricingId}`, 'v2');

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete Pricing API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to delete pricing (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Pricing deleted successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting pricing:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete pricing'
      };
    }
  },

  /**
   * 🚫 Deactivate airport transfer pricing (soft delete)
   */
  async deactivatePricing(pricingId: string): Promise<PricingResponse> {
    return this.updatePricing(pricingId, { isAvailable: false });
  },

  /**
   * 📊 Get pricing statistics
   */
  async getPricingStats(): Promise<PricingResponse> {
    try {
      const url = buildApiUrl('/admin/airport-pricing/stats', 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Pricing Stats API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch pricing stats (${response.status})`);
      }

      const result = await response.json();

      const stats = result.stats || result.data?.stats || result.data;

      return {
        success: true,
        stats: stats
      };
    } catch (error) {
      console.error('❌ Error fetching pricing stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch pricing stats'
      };
    }
  },

  /**
   * 📜 Get pricing history and audit trail
   */
  async getPricingHistory(pricingId: string): Promise<PricingResponse> {
    try {
      const url = buildApiUrl(`/admin/airport-pricing/${pricingId}/history`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Pricing History API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch pricing history (${response.status})`);
      }

      const result = await response.json();

      const history = result.history || result.data?.history || result.data || [];

      return {
        success: true,
        history: Array.isArray(history) ? history : []
      };
    } catch (error) {
      console.error('❌ Error fetching pricing history:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch pricing history'
      };
    }
  }
};
